/** `both` / Either = agent discretion between score and profit/day. */
export type PaperRankBy = "score" | "profit_day" | "both";

/** Wheel = CSP→CC style gates; discretion = free selection from sources. */
export type PaperStrategy = "wheel" | "discretion";

export interface PaperAgentSettings {
  enabled: boolean;
  initialCapital: number;
  expectedMonthlyRocPct: number;
  /** Bias selection/sizing to try beating expected monthly ROC. */
  tryOverperform: boolean;
  perTradeFee: number;
  /** Fixed HH:MM list, interval every N minutes, or manual (Run only). */
  scheduleMode: "slots" | "interval" | "manual";
  slots: string[];
  intervalMinutes: number;
  /** HH:MM Pacific — first run of the day for interval mode. */
  intervalStart: string;
  /**
   * wheel = puts when no owed/long shares, covered calls when free owed shares exist;
   * discretion = open from Sources without wheel phase gates.
   */
  strategy: PaperStrategy;
  sources: Array<"puts" | "calls">;
  rankBy: PaperRankBy;
  /** Max new opens per timeslot when discretion is off. */
  topNPerSlot: number;
  /** Agent picks opens-per-slot count (1–3 within room). */
  topNPerSlotDiscretion: boolean;
  maxOpenPositions: number;
  maxContractsPerTrade: number;
  minPop: number;
  minAnnRoc: number;
  /** Max DTE for new put opens. 0 = no max. */
  maxOpenDtePuts: number;
  /** Max DTE for new call opens. 0 = no max. */
  maxOpenDteCalls: number;
  /** Same as Puts/Calls screener Tradable (mid/last + bid/ask). */
  tradableOnly: boolean;
  /**
   * Expand beyond SF call screener via Market Data (Calls+ style) and allow
   * strikes below avg when stock is underwater and PoP meets riskyCallsMinPop.
   */
  allowRiskyCalls: boolean;
  riskyCallsMinPop: number;
  preferFocusTickers: boolean;
  focusTickers: string[];
  takeProfitPctOfCredit: number;
  /** Agent picks take-profit % (about 25–75) from DTE / PoP / outperform. */
  takeProfitDiscretion: boolean;
  /** stop | keep (ok with assignment) | either (agent discretion). */
  stopLossMode: "stop" | "keep" | "either";
  stopLossPctOfCredit: number;
  maxHoldDte: number;
  closeAtExpiry: boolean;
  /**
   * Cursor SDK model for the decision layer: `auto` or a concrete model key
   * (e.g. `claude-sonnet-5`). Setting key kept as claudeModel for compatibility.
   */
  claudeModel: PaperClaudeModel | string;
  /**
   * When true, persist Cursor agent id and resume on later slots/advise calls.
   * Default false (fresh agent each time).
   */
  keepCursorAgentBetweenSessions?: boolean;
  /** Auto-recycle after N slot/manual runs since last recycle. 0 = off. Recommended: 20. */
  autoRecycleEveryRuns?: number;
  /** Auto-recycle when a single run uses at least this many Cursor tokens. 0 = off. Recommended: 1500000. */
  autoRecycleMinRunTokens?: number;
  /**
   * When true, Cursor cloud advise may browse/search for catalysts before deciding.
   * Default false (JSON-only from packed SF+ data).
   */
  allowWeb?: boolean;
  /** Prior advise turns injected as Linode-owned memory (0–20). */
  cursorMemoryTurns?: number;
  /**
   * Free-text strategy notes / prompt for this agent.
   * Stored per agent; used as instructions for the Cursor decision layer.
   */
  instructions: string;
  /**
   * Selected user-level cloud prompt template id.
   * Defaults to built-in Wheel (default).
   */
  promptTemplateId?: string;
}

export interface PromptTemplate {
  id: string;
  name: string;
  /** Short slot prompt (every advise). */
  body: string;
  /** Standing bootstrap prompt (Agent.create only). */
  bootstrapBody: string;
  builtIn?: boolean;
  updatedAt: string;
}

/** Per-agent Cursor model setting (`auto` resolves to Sonnet). */
export type PaperClaudeModel =
  | "auto"
  | "claude-haiku-4-5"
  | "claude-sonnet-5"
  | "claude-opus-5";

export const PAPER_CLAUDE_MODEL_OPTIONS: Array<{
  id: PaperClaudeModel;
  label: string;
}> = [
  { id: "auto", label: "Auto" },
  { id: "claude-haiku-4-5", label: "Haiku 4.5" },
  { id: "claude-sonnet-5", label: "Sonnet" },
  { id: "claude-opus-5", label: "Opus" },
];

export type PaperAgentColor =
  | "slate"
  | "blue"
  | "teal"
  | "green"
  | "amber"
  | "orange"
  | "rose"
  | "violet";

export const PAPER_AGENT_COLOR_OPTIONS: Array<{
  id: PaperAgentColor;
  hex: string;
  label: string;
}> = [
  { id: "slate", hex: "#64748b", label: "Slate" },
  { id: "blue", hex: "#3b82f6", label: "Blue" },
  { id: "teal", hex: "#14b8a6", label: "Teal" },
  { id: "green", hex: "#22c55e", label: "Green" },
  { id: "amber", hex: "#f59e0b", label: "Amber" },
  { id: "orange", hex: "#f97316", label: "Orange" },
  { id: "rose", hex: "#f43f5e", label: "Rose" },
  { id: "violet", hex: "#8b5cf6", label: "Violet" },
];

export interface PaperAgentMeta {
  id: string;
  name: string;
  color: PaperAgentColor | string;
  colorHex: string;
  enabled: boolean;
  hasSnapshot: boolean;
  equity: number;
  openPositions: number;
  /** Ledger account this paper book's cash is shown under. */
  accountId?: string | null;
  /** Spendable cash on the paper book. */
  buyingPower?: number | null;
  /** Latest trade / toggle timestamp for chrome unread badge. */
  lastActionAt?: string | null;
  kind?: "paper" | "advisor";
  executionMode?: "paper" | "advise" | "live";
  lastAdviceAt?: string | null;
  scheduleMode?: PaperAgentSettings["scheduleMode"];
  runInFlight?: boolean;
  runInFlightStartedAt?: string | null;
  runInFlightMode?: string | null;
}

export interface PaperAgentsListResponse {
  ok: boolean;
  activeId: string | null;
  agents: PaperAgentMeta[];
}

export interface PaperAgentIncomingShare {
  ownerUserId: string;
  ownerEmail: string | null;
  agentId: string;
  agentName: string;
  agentColor: string;
  agentColorHex: string;
  role: "view";
  grantedAt: string;
}

export interface PaperAgentOutgoingShare {
  agentId: string;
  viewerEmail: string;
  viewerUserId: string;
  role: "view";
  createdAt: string;
  createdBy: string;
}

export interface PaperAgentIncomingSharesResponse {
  ok: boolean;
  grants: PaperAgentIncomingShare[];
}

export interface PaperAgentOutgoingSharesResponse {
  ok: boolean;
  grants: PaperAgentOutgoingShare[];
}

export interface PaperPosition {
  id: string;
  underlying: string;
  side: string;
  strike: number;
  expirationDate: string;
  optionSymbol: string;
  quantity: number;
  entryPremium: number;
  mark: number;
  markUpdatedAt: string | null;
  collateral: number;
  openedAt: string;
  openSlot: string | null;
  annRoc: number;
  pop: number;
  justification?: string;
  unrealizedPnl?: number;
  dte?: number | null;
}

export interface PaperFill {
  id: string;
  ts: string;
  action: string;
  reason: string;
  justification?: string;
  /** Strategy active when the open/close decision was made. */
  strategy?: PaperStrategy | string;
  underlying: string;
  side: string;
  strike: number;
  expirationDate: string;
  optionSymbol: string;
  quantity: number;
  premium: number;
  /** Open premium per share — set on close fills. */
  entryPremium?: number;
  fee: number;
  pnl: number;
  /** Call-assignment P/L vs share avg at that moment; 0 on puts / expiry / BTC. */
  assignIncome?: number;
  userStatus?: AdviceUserStatus;
  userStatusAt?: string;
  userNote?: string;
}

export interface PaperAdviseFeedback {
  questions: string[];
  promptIdeas: string[];
  notes: string;
}

export interface PaperRunLogSummary {
  analysis: string;
  ideas: string[];
  decisions: string[];
  /** Optional Cursor feedback loop (questions / prompt ideas). */
  feedback?: PaperAdviseFeedback | null;
}

export interface PaperRunLogActivity {
  day: string | null;
  enabled: boolean;
  traded: boolean;
  toggled: boolean;
}

export interface PaperWebQuery {
  query: string;
  purpose?: string;
}

export interface PaperRunLogEntry {
  id?: string;
  ts: string;
  message: string;
  slot?: string | null;
  /** Market Data tokens/credits used during this run (always set; 0 when unused). */
  creditsUsed?: number;
  /** Claude / Cursor tokens used during this run (always set; 0 when unused). */
  claudeTokens?: number;
  actions?: { closed: number; opened: number; marked: number; held: number };
  summary?: PaperRunLogSummary;
  /** Pacific-day agent activity (enabled / traded / toggled) for this run. */
  activity?: PaperRunLogActivity;
  /** advise-test / dry-run marker (slot cycles omit this). */
  kind?: "advise-test" | "recycle" | "test" | "dry-run" | "slot" | "advice";
  dryRun?: boolean;
  /** When true, omit from monthly runs/token totals (recycle ops). */
  excludeFromStats?: boolean;
  /** Whether Allow Web was on for this Cursor advise (A/B token compares). */
  allowWeb?: boolean;
  /** Agent-reported web research usage (Allow Web runs). */
  webUsed?: boolean;
  /** Distinct searches/URLs the agent looked up when webUsed. */
  webQueries?: PaperWebQuery[];
  ok?: boolean;
  model?: string;
  runtime?: string;
  error?: string;
  requestedAt?: string;
  respondedAt?: string;
  durationMs?: number;
  tokensIn?: number;
  tokensOut?: number;
  tokensTotal?: number;
  cacheReadTokens?: number;
  cacheWriteTokens?: number;
  reasoningTokens?: number;
  cursorAgentId?: string;
  cursorRunId?: string;
  /** Link to paper-cursor-log entry. */
  logId?: string;
  /** Full Cursor decision payload (advise-test). */
  decision?: Record<string, unknown> | null;
  /** What executed vs skipped (dry-run notes). */
  applied?: Record<string, unknown> | null;
  /** System prompt Linode sent to Cursor (advise/cursor runs). */
  system?: string;
  /** Packed user prompt / context Linode sent to Cursor. */
  user?: string;
  /** Bootstrap-only user prompt (create path). */
  bootstrapUser?: string;
  /** Bootstrap turn LLM reply (usually {"ok":true}). */
  bootstrapResponseText?: string;
  /** Link to bootstrap paper-cursor-log entry. */
  bootstrapLogId?: string;
  bootstrapUsed?: boolean;
  /** Raw LLM reply text from the cloud agent. */
  responseText?: string;
}

/** Long stock from paper put assignments (private agent ledger). */
export interface PaperShareHolding {
  ticker: string;
  quantity: number;
  /** Shares free of open short calls on this agent's book. */
  available: number;
  avgCost: number | null;
  costBasis: number | null;
}

export interface PaperPortfolioSummary {
  cash: number;
  buyingPower?: number;
  realizedPnl: number;
  reserved: number;
  unrealized: number;
  openValue: number;
  equity: number;
  positions: PaperPosition[];
  /** Owed/long shares from put assignments (and call-aways). */
  shares?: PaperShareHolding[];
  fills: PaperFill[];
  equityCurve: Array<{ ts: string; cash: number; openValue: number; equity: number }>;
  runLog: PaperRunLogEntry[];
}

export interface PaperAgentSnapshot {
  capturedAt: string;
  reason: string;
  name: string;
  settings: PaperAgentSettings;
  portfolio: PaperPortfolioSummary;
  runtime: {
    lastSlotRuns: Record<string, string>;
    lastManageAt: string | null;
    lastOpenAt: string | null;
    lastError: string | null;
    initializedAt: string | null;
  };
}

export interface PaperAgentMonthBook {
  month: string;
  equityBom: number;
  /** Analytics-style own capital: `equityBom + income − fees` (no mark-to-market). */
  equityEom: number;
  /** Open-month premiums − BTC paid this month + this month's call assignment P&L. */
  income: number;
  premiums: number;
  /** BTC buybacks attributed to the month they were paid. */
  premiumCorrections?: number;
  btcCost: number;
  /** Call-away P&L, `(strike − share avg cost) × shares`, in the assignment month. */
  assignIncome?: number;
  /** Closed-trade net P&L (includes fees on closes). */
  realizedPnl: number;
  fees: number;
  opens: number;
  closes: number;
  premiumCredits: number;
  targetRocPct: number;
  rocPct: number | null;
  /** Market Data tokens used by agent runs in this month. */
  mdTokens?: number;
  /** Claude tokens used by agent runs in this month. */
  claudeTokens?: number;
  /** Documented agent runs in this month. */
  runs?: number;
}

export interface PaperAgentStats {
  selectedMonth: string;
  currentMonth: string;
  /** Agent run-log token totals for the current Pacific calendar day. */
  todayTokens?: {
    mdTokens: number;
    claudeTokens: number;
    runs?: number;
  };
  months: PaperAgentMonthBook[];
  monthDetail: PaperAgentMonthBook & {
    targetIncome: number;
    progressPct: number | null;
    openPositions: number;
  };
  total: {
    /** Own capital at the end of the selected month: start + income − fees. */
    equity: number;
    startEquity: number;
    income: number;
    premiums: number;
    premiumCorrections?: number;
    btcCost: number;
    assignIncome?: number;
    realizedPnl: number;
    fees: number;
    opens: number;
    closes: number;
    rocPct: number | null;
    initialCapital: number;
    monthsTraded: number;
    /** All-time Market Data tokens for this agent. */
    mdTokens?: number;
    /** All-time Claude tokens for this agent. */
    claudeTokens?: number;
    /** Documented runs through the selected month. */
    runs?: number;
    /** Selected month the cumulative totals are through (YYYY-MM). */
    throughMonth?: string;
  };
}

export type AdvisorAction = "open" | "close" | "hold" | "skip";

export type AdviceUserStatus = "pending" | "accepted" | "dismissed";

/** Order intent from an advisor (same shape a future live executor can consume). */
export interface AdvisorRecommendation {
  id: string;
  ts: string;
  action: AdvisorAction | string;
  reason: string;
  justification?: string;
  underlying: string;
  side: string;
  strike: number;
  expirationDate: string;
  optionSymbol: string;
  quantity: number;
  premium: number;
  fee?: number;
  pnl?: number;
  agentId?: string;
  agentName?: string;
  slot?: string;
  summary?: string;
  userStatus?: AdviceUserStatus;
  userStatusAt?: string;
  userNote?: string;
}

export interface AdvisorAdviceState {
  updatedAt: string | null;
  summary: string;
  pass: boolean;
  /** advise = record intents only. Reserved: live broker execution (not enabled). */
  execution: "advise" | "live" | string;
  recommendations: AdvisorRecommendation[];
  history: AdvisorRecommendation[];
  liveBook?: Record<string, unknown> | null;
}

export interface LatestAdviceResponse {
  ok: boolean;
  agents: PaperAgentMeta[];
  recommendations: AdvisorRecommendation[];
  updatedAt: string | null;
  inflight?: Array<{
    agentId: string;
    agentName: string;
    startedAt: string;
    mode: string;
  }>;
}

export interface PaperAgentStateResponse {
  ok: boolean;
  /** Present when viewing someone else's shared agent. */
  accessMode?: "owner" | "viewer";
  sharedBy?: {
    userId: string;
    email: string | null;
  };
  agentId: string | null;
  name: string;
  color: PaperAgentColor | string;
  colorHex: string;
  kind?: "paper" | "advisor";
  executionMode?: "paper" | "advise" | "live";
  runInFlight?: boolean;
  runInFlightStartedAt?: string | null;
  runInFlightMode?: string | null;
  activeId: string | null;
  agents: PaperAgentMeta[];
  settings: PaperAgentSettings;
  portfolio: PaperPortfolioSummary;
  advice?: AdvisorAdviceState;
  /**
   * Full private paper transaction ledger for this agent (newest first).
   * Survives rolling fill-window truncation and agent resets.
   */
  transactions?: PaperFill[];
  runtime: {
    lastSlotRuns: Record<string, string>;
    lastManageAt: string | null;
    lastOpenAt: string | null;
    lastError: string | null;
    cursorSdkAgentId?: string | null;
    initializedAt: string | null;
  };
  lastSnapshot?: PaperAgentSnapshot | null;
  stats?: PaperAgentStats;
  marketdata?: {
    configured: boolean;
    dailyBudget: number;
    creditsUsedToday: number;
    creditsRemaining: number;
  };
  /** Cursor SDK advisor usage + config. */
  cursor?: {
    configured: boolean;
    monthlyBudget?: number;
    dailyBudget: number;
    tokensUsedThisMonth?: number;
    tokensUsedToday: number;
    tokensRemaining: number;
    month?: string;
    renewsOn?: string;
    resolvedModel?: string;
    models?: Array<{ id: string; label: string; apiId: string; hint?: string }>;
    keepCursorAgentBetweenSessions?: boolean;
    resumeAgentId?: string | null;
    runtime?: {
      configured: boolean;
      runtime: "local" | "cloud";
      modelDefault?: string;
      localCwd?: string | null;
      cloudRepo?: string | null;
      cloudEmptyWorkspace?: boolean;
    };
  };
  /** @deprecated alias of cursor — older bundles. */
  claude?: {
    configured: boolean;
    /** Monthly Cursor token allowance. */
    monthlyBudget?: number;
    /** @deprecated use monthlyBudget */
    dailyBudget: number;
    tokensUsedThisMonth?: number;
    /** @deprecated use tokensUsedThisMonth */
    tokensUsedToday: number;
    tokensRemaining: number;
    /** Pacific calendar month key (YYYY-MM). */
    month?: string;
    /** Next allowance renewal date (YYYY-MM-DD, Pacific month boundary). */
    renewsOn?: string;
    /** API model id after resolving Auto. */
    resolvedModel?: string;
    models?: Array<{ id: string; label: string; apiId: string; hint?: string }>;
    keepCursorAgentBetweenSessions?: boolean;
    resumeAgentId?: string | null;
  };
  nextSlot: { slot: string; label: string } | null;
  marketOpenHint?: string;
  /** Header token cards — totals across all paper agents. */
  tokenTotals?: {
    mdUsedToday: number;
    cursorUsedThisMonth: number;
  };
  result?: unknown;
}

async function parseJson<T>(res: Response): Promise<T> {
  const body = (await res.json().catch(() => ({}))) as T & {
    detail?: { message?: string } | string;
  };
  if (!res.ok) {
    const detail = body?.detail;
    const message =
      typeof detail === "string"
        ? detail
        : detail && typeof detail === "object" && typeof detail.message === "string"
          ? detail.message
          : `Request failed (${res.status})`;
    throw new Error(message);
  }
  return body;
}

function withAgentQuery(path: string, agentId?: string | null): string {
  if (!agentId) return path;
  const join = path.includes("?") ? "&" : "?";
  return `${path}${join}agentId=${encodeURIComponent(agentId)}`;
}

export async function fetchPaperAgentState(
  agentId?: string | null,
  month?: string | null,
  opts?: { refreshMarks?: boolean },
): Promise<PaperAgentStateResponse> {
  let path = withAgentQuery("/agents/paper/state", agentId);
  if (month && /^\d{4}-\d{2}$/.test(month)) {
    const join = path.includes("?") ? "&" : "?";
    path = `${path}${join}month=${encodeURIComponent(month)}`;
  }
  if (opts?.refreshMarks === false) {
    const join = path.includes("?") ? "&" : "?";
    path = `${path}${join}refreshMarks=0`;
  }
  const res = await fetch(path, {
    method: "GET",
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  return parseJson(res);
}

export async function fetchPaperAgentsList(): Promise<PaperAgentsListResponse> {
  const res = await fetch("/agents/paper/agents", {
    method: "GET",
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  return parseJson(res);
}

export async function fetchPaperAgentDefaults(): Promise<PaperAgentSettings> {
  const res = await fetch("/agents/paper/defaults", {
    method: "GET",
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  const body = await parseJson<{ ok: boolean; settings: PaperAgentSettings }>(res);
  return body.settings;
}

export async function savePaperAgentSettings(
  settings: Partial<PaperAgentSettings> & { name?: string; color?: string },
  agentId?: string | null,
): Promise<PaperAgentSettings> {
  const res = await fetch(withAgentQuery("/agents/paper/settings", agentId), {
    method: "PUT",
    credentials: "include",
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    body: JSON.stringify({ ...settings, agentId: agentId ?? undefined }),
  });
  const body = await parseJson<{ ok: boolean; settings: PaperAgentSettings }>(res);
  return body.settings;
}

export async function resetPaperAgent(
  patch?: Partial<PaperAgentSettings> & { name?: string },
  agentId?: string | null,
): Promise<PaperAgentStateResponse> {
  const res = await fetch(withAgentQuery("/agents/paper/reset", agentId), {
    method: "POST",
    credentials: "include",
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    body: JSON.stringify({ ...(patch ?? {}), agentId: agentId ?? undefined }),
  });
  return parseJson(res);
}

export async function recyclePaperAgent(
  agentId?: string | null,
): Promise<
  PaperAgentStateResponse & {
    runLog?: PaperRunLogEntry;
    advise?: {
      ok?: boolean;
      error?: string;
      bootstrapUsed?: boolean;
      cursorAgentId?: string;
      tokensTotal?: number;
      tokensIn?: number;
      tokensOut?: number;
      bootstrapTokens?: number;
      bookSyncTokens?: number;
    };
  }
> {
  const res = await fetch(withAgentQuery("/agents/paper/recycle", agentId), {
    method: "POST",
    credentials: "include",
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    body: JSON.stringify({ agentId: agentId ?? undefined }),
  });
  return parseJson(res);
}

export async function runPaperAgentNow(
  patch?: Partial<PaperAgentSettings> & {
    name?: string;
    forceOpen?: boolean;
    forceMarketOpen?: boolean;
  },
  agentId?: string | null,
): Promise<PaperAgentStateResponse> {
  const res = await fetch(withAgentQuery("/agents/paper/run-now", agentId), {
    method: "POST",
    credentials: "include",
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    body: JSON.stringify({ ...(patch ?? {}), agentId: agentId ?? undefined }),
  });
  return parseJson(res);
}

export async function fetchLatestAdvice(): Promise<LatestAdviceResponse> {
  const res = await fetch("/agents/paper/advice", {
    method: "GET",
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  return parseJson(res);
}

export async function runAgentAdvice(agentId?: string | null): Promise<PaperAgentStateResponse> {
  const res = await fetch(withAgentQuery("/agents/paper/run-advice", agentId), {
    method: "POST",
    credentials: "include",
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    body: JSON.stringify({ agentId: agentId ?? undefined }),
  });
  return parseJson(res);
}

export async function updateAdviceStatus(
  status: "accepted" | "dismissed",
  recIds: string[],
  agentId?: string | null,
  note?: string,
): Promise<PaperAgentStateResponse> {
  const res = await fetch(withAgentQuery("/agents/paper/advice/status", agentId), {
    method: "POST",
    credentials: "include",
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    body: JSON.stringify({
      agentId: agentId ?? undefined,
      status,
      recIds,
      ...(note ? { note } : {}),
    }),
  });
  return parseJson(res);
}

export async function createPaperAgent(opts?: {
  name?: string;
  cloneFromId?: string;
  /** When true (or cloneFromId set), copy settings + instructions from the source agent. */
  clone?: boolean;
  /** paper (default) or advisor. Clone copies the source kind unless set. */
  kind?: "paper" | "advisor";
  settings?: Partial<Pick<PaperAgentSettings, "initialCapital">>;
}): Promise<PaperAgentStateResponse> {
  const res = await fetch("/agents/paper/agents", {
    method: "POST",
    credentials: "include",
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    body: JSON.stringify(opts ?? {}),
  });
  return parseJson(res);
}

export async function renamePaperAgent(
  agentId: string,
  name: string,
): Promise<PaperAgentStateResponse> {
  const res = await fetch(`/agents/paper/agents/${encodeURIComponent(agentId)}`, {
    method: "PATCH",
    credentials: "include",
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    body: JSON.stringify({ name }),
  });
  return parseJson(res);
}

export async function deletePaperAgent(agentId: string): Promise<PaperAgentStateResponse> {
  const res = await fetch(`/agents/paper/agents/${encodeURIComponent(agentId)}`, {
    method: "DELETE",
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  return parseJson(res);
}

export async function setActivePaperAgent(agentId: string): Promise<PaperAgentStateResponse> {
  const res = await fetch("/agents/paper/active", {
    method: "PUT",
    credentials: "include",
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    body: JSON.stringify({ agentId }),
  });
  return parseJson(res);
}

export async function fetchPaperAdviseLog(
  agentId?: string | null,
  limit = 40,
): Promise<{
  ok: boolean;
  agentId: string;
  cursorConfigured: boolean;
  resumeAgentId: string | null;
  entries: Array<Record<string, unknown>>;
  updatedAt: string | null;
}> {
  const path = withAgentQuery(
    `/agents/paper/advise-log?limit=${encodeURIComponent(String(limit))}`,
    agentId,
  );
  const res = await fetch(path, {
    method: "GET",
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  return parseJson(res);
}

export async function fetchPromptTemplates(): Promise<{
  ok: boolean;
  templates: PromptTemplate[];
  updatedAt: string | null;
  placeholderLegend: string;
}> {
  const res = await fetch("/agents/paper/prompt-templates", {
    method: "GET",
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  return parseJson(res);
}

export async function createPromptTemplate(opts?: {
  name?: string;
  body?: string;
  bootstrapBody?: string;
  duplicateFromId?: string;
}): Promise<{ ok: boolean; template: PromptTemplate; templates: PromptTemplate[] }> {
  const res = await fetch("/agents/paper/prompt-templates", {
    method: "POST",
    credentials: "include",
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    body: JSON.stringify(opts ?? {}),
  });
  return parseJson(res);
}

export async function updatePromptTemplate(
  templateId: string,
  patch: { name?: string; body?: string; bootstrapBody?: string },
): Promise<{ ok: boolean; template: PromptTemplate; templates: PromptTemplate[] }> {
  const res = await fetch(
    `/agents/paper/prompt-templates/${encodeURIComponent(templateId)}`,
    {
      method: "PUT",
      credentials: "include",
      headers: { Accept: "application/json", "Content-Type": "application/json" },
      body: JSON.stringify(patch),
    },
  );
  return parseJson(res);
}

export async function resetPromptTemplate(
  templateId: string,
): Promise<{ ok: boolean; template: PromptTemplate; templates: PromptTemplate[] }> {
  const res = await fetch(
    `/agents/paper/prompt-templates/${encodeURIComponent(templateId)}/reset`,
    {
      method: "POST",
      credentials: "include",
      headers: { Accept: "application/json", "Content-Type": "application/json" },
      body: JSON.stringify({}),
    },
  );
  return parseJson(res);
}

export async function deletePromptTemplate(
  templateId: string,
): Promise<{ ok: boolean; deletedId: string; templates: PromptTemplate[] }> {
  const res = await fetch(
    `/agents/paper/prompt-templates/${encodeURIComponent(templateId)}`,
    {
      method: "DELETE",
      credentials: "include",
      headers: { Accept: "application/json" },
    },
  );
  return parseJson(res);
}

/** Dry-run Cursor advise (fixture candidates) — no paper fills; writes a test run-log entry. */
export async function runPaperAdviseTest(
  opts?: { want?: number; resume?: boolean; slot?: string },
  agentId?: string | null,
): Promise<{
  ok: boolean;
  agentId: string;
  runLog?: PaperRunLogEntry;
  advise: {
    ok: boolean;
    tokensIn: number;
    tokensOut: number;
    tokensTotal: number;
    durationMs: number;
    cursorAgentId: string;
    cursorRunId: string;
    resumed: boolean;
    advice: unknown;
    error: string | null;
    responseText: string;
    logId?: string | null;
  };
}> {
  const res = await fetch(withAgentQuery("/agents/paper/advise-test", agentId), {
    method: "POST",
    credentials: "include",
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    body: JSON.stringify({ ...(opts ?? {}), agentId: agentId ?? undefined }),
  });
  return parseJson(res);
}

export async function fetchSharedPaperAgents(): Promise<PaperAgentIncomingSharesResponse> {
  const res = await fetch("/agents/paper/shared", {
    method: "GET",
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  return parseJson(res);
}

export async function fetchOutgoingPaperAgentShares(
  agentId?: string | null,
): Promise<PaperAgentOutgoingSharesResponse> {
  let path = "/agents/paper/shared/outgoing";
  if (agentId) path = `${path}?agentId=${encodeURIComponent(agentId)}`;
  const res = await fetch(path, {
    method: "GET",
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  return parseJson(res);
}

export async function fetchSharedPaperAgentState(
  ownerUserId: string,
  agentId: string,
  month?: string | null,
): Promise<PaperAgentStateResponse> {
  let path = `/agents/paper/shared/${encodeURIComponent(ownerUserId)}/${encodeURIComponent(agentId)}/state`;
  if (month && /^\d{4}-\d{2}$/.test(month)) {
    path = `${path}?month=${encodeURIComponent(month)}&refreshMarks=0`;
  } else {
    path = `${path}?refreshMarks=0`;
  }
  const res = await fetch(path, {
    method: "GET",
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  return parseJson(res);
}

export async function grantPaperAgentView(
  agentId: string,
  email: string,
): Promise<{ ok: boolean; grant: PaperAgentOutgoingShare }> {
  const res = await fetch(`/agents/paper/agents/${encodeURIComponent(agentId)}/share`, {
    method: "POST",
    credentials: "include",
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    body: JSON.stringify({ email }),
  });
  return parseJson(res);
}

export async function revokePaperAgentView(
  agentId: string,
  viewerUserId: string,
): Promise<{ ok: boolean; agentId: string; viewerUserId: string }> {
  const res = await fetch(
    `/agents/paper/agents/${encodeURIComponent(agentId)}/share/${encodeURIComponent(viewerUserId)}`,
    {
      method: "DELETE",
      credentials: "include",
      headers: { Accept: "application/json" },
    },
  );
  return parseJson(res);
}

/** Build a link to the standalone observer page (StockForge+ sign-in required). */
export function paperAgentObserverLink(ownerUserId: string, agentId: string): string {
  const params = new URLSearchParams({ owner: ownerUserId, agent: agentId });
  return `/web/agent-view.html?${params.toString()}`;
}
